import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader, Sparkles, Crown, Zap } from 'lucide-react';
import { CILogo } from '../components/CILogo';
import { stripeService } from '../services/stripe';

export default function CheckoutSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Truthful states. Nothing here says the plan is active unless the backend confirmed it.
  //   claiming   : signed in, linking a paid public checkout to the account
  //   claimed    : backend confirmed the plan is linked to this account
  //   needs_login: server VERIFIED the public checkout is paid; waiting for sign-in (nothing is applied yet)
  //   unconfirmed: public checkout waiting for sign-in but the server could not verify payment (not paid or unknown)
  //   retry      : could not confirm yet (network, provider, payment still completing); the checkout details are kept
  //   failed     : the link was refused; contact support with the Session ID
  //   received   : signed-in checkout; the plan is applied when the payment provider confirms (not confirmed here)
  //   unlinked   : this browser has no checkout details and is signed out; payment status unknown here
  const [status, setStatus] = useState('claiming');
  const sessionId = searchParams.get('session_id');
  const plan = searchParams.get('plan');
  const billing = searchParams.get('billing');

  const hasToken = () => !!(sessionStorage.getItem('session_token') || localStorage.getItem('ci_session_token'));

  const runClaim = React.useCallback(async () => {
    if (!stripeService.hasPendingCheckout()) {
      setStatus(hasToken() ? 'received' : 'unlinked');
      return;
    }
    if (!hasToken()) {
      const st = await stripeService.pendingCheckoutStatus();
      setStatus(st === 'paid' ? 'needs_login' : 'unconfirmed');
      return;
    }
    setStatus('claiming');
    const r = await stripeService.claimPendingCheckout();
    setStatus(r.state === 'claimed' ? 'claimed' : r.state === 'retry' ? 'retry' : r.state === 'failed' ? 'failed'
      : r.state === 'signed_out' ? 'unconfirmed' : 'received');
  }, []);

  useEffect(() => { runClaim(); }, [runClaim]);

  const getPlanDetails = () => {
    switch (plan) {
      case 'starter':
        return {
          name: 'Starter Plan',
          icon: <Sparkles className="w-6 h-6 text-blue-500" />,
          color: 'blue',
          features: ['Unlimited products & deals', 'Unlimited thedeals.ai publishing', 'AI recommendations', 'Shopify auto-sync', '500 msgs/mo included']
        };
      case 'pro':
        return {
          name: 'Pro Plan',
          icon: <Zap className="w-6 h-6 text-emerald-500" />,
          color: 'emerald',
          features: ['Everything in Starter', 'Slack approvals & decision traces', 'Unlimited AI recommendations', 'Precedent library', '2,000 msgs/mo included']
        };
      case 'scale':
        return {
          name: 'Scale Plan',
          icon: <Crown className="w-6 h-6 text-purple-500" />,
          color: 'purple',
          features: ['Everything in Pro', 'Signals API & team workflows', 'Funding pack & USDC payouts', 'Advanced guardrails', '10,000 msgs/mo included']
        };
      case 'intelligence-suite': // legacy plan name → maps to Pro
        return {
          name: 'Pro Plan',
          icon: <Crown className="w-6 h-6 text-purple-500" />,
          color: 'purple',
          features: ['Unlimited products & deals', 'Unlimited AI recommendations', 'Real-time price intelligence']
        };
      default:
        return {
          name: 'Free Plan',
          icon: <Sparkles className="w-6 h-6 text-[#3EF1C7]" />,
          color: 'teal',
          features: ['10 products', '5 deals/month', 'Basic analytics']
        };
    }
  };

  const planDetails = getPlanDetails();

  const handleContinue = () => {
    // Redirect to merchant login with plan info
    navigate(`/merchant/login?subscribed=true&plan=${plan || 'free'}&billing=${billing || 'monthly'}`);
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#0B1120' }}>
      <div className="max-w-md w-full mx-auto px-6">
        <div className="rounded-2xl border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-8 text-center">
          <div className="mb-6">
            <CILogo size="lg" showText={false} variant="color" className="mx-auto" />
          </div>

          {status === 'claiming' && (
            <>
              <Loader className="w-16 h-16 text-[#3EF1C7] animate-spin mx-auto mb-6" />
              <h1 className="text-2xl font-bold text-white mb-3">Linking your payment...</h1>
              <p className="text-[#9CB0C7]">Please wait while we attach it to your account</p>
            </>
          )}

          {(status === 'claimed' || status === 'received') && (
            <>
              <div className="w-16 h-16 rounded-full bg-[#8CF78E]/10 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-[#8CF78E]" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-3">
                {status === 'claimed' ? 'Payment linked to your account' : 'Checkout complete'}
              </h1>
              <p className="text-[#9CB0C7] mb-6" data-testid="checkout-status-text">
                {status === 'claimed'
                  ? `Your ${planDetails.name} is now linked to your account.`
                  : `Your ${planDetails.name} will appear on your account once the payment provider confirms it. Check Billing if it does not show up shortly.`}
              </p>
              <div className="rounded-lg bg-[#3EF1C7]/10 border border-[#3EF1C7]/30 p-4 mb-6">
                <div className="flex items-center justify-center gap-2 mb-3">
                  {planDetails.icon}
                  <span className="font-bold text-white">{planDetails.name}</span>
                </div>
                <ul className="text-sm text-[#9CB0C7] space-y-1 text-left">
                  {planDetails.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-[#8CF78E]" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full py-3 bg-gradient-to-r from-[#3EF1C7] to-[#7A5CFF] text-white rounded-lg hover:opacity-90 transition-opacity font-semibold flex items-center justify-center gap-2"
                data-testid="continue-button"
              >
                <Sparkles className="w-5 h-5" />
                Continue to Dashboard
              </button>
              <p className="text-xs text-[#9CB0C7] mt-3">Session ID: {sessionId || 'N/A'}</p>
            </>
          )}

          {status === 'needs_login' && (
            <>
              <h1 className="text-2xl font-bold text-white mb-3">Payment confirmed. Sign in to finish.</h1>
              <p className="text-[#9CB0C7] mb-6" data-testid="checkout-status-text">
                We verified your payment, but it is not linked to an account yet, so your {planDetails.name} is not active. Sign in or sign up
                in this browser and we will link it automatically.
              </p>
              <button
                onClick={handleContinue}
                className="w-full py-3 bg-gradient-to-r from-[#3EF1C7] to-[#7A5CFF] text-white rounded-lg font-semibold"
                data-testid="signin-to-link-button"
              >
                Sign in to link payment
              </button>
              <p className="text-xs text-[#9CB0C7] mt-3">Session ID: {sessionId || 'N/A'}</p>
            </>
          )}

          {(status === 'retry' || status === 'unconfirmed') && (
            <>
              <h1 className="text-2xl font-bold text-white mb-3">We could not confirm your payment yet</h1>
              <p className="text-[#9CB0C7] mb-6" data-testid="checkout-status-text">
                We still have your checkout details in this browser, but we could not confirm the payment. Try again in a
                moment. If this keeps happening, contact support with the Session ID.
              </p>
              <button onClick={runClaim} className="w-full py-3 bg-[#7A5CFF] text-white rounded-lg font-semibold" data-testid="retry-claim-button">
                Try again
              </button>
              <p className="text-xs text-[#9CB0C7] mt-3">Session ID: {sessionId || 'N/A'}</p>
            </>
          )}

          {(status === 'failed' || status === 'unlinked') && (
            <>
              <h1 className="text-2xl font-bold text-white mb-3">
                {status === 'failed' ? 'We could not link this checkout' : 'We could not find this checkout in this browser'}
              </h1>
              <p className="text-[#9CB0C7] mb-6" data-testid="checkout-status-text">
                {status === 'failed' ? 'This checkout was not linked to your account.' : 'We cannot confirm the payment status from here.'} If you completed a payment, contact support and include the Session ID below.
              </p>
              <p className="text-xs text-[#9CB0C7] mb-3">Session ID: {sessionId || 'N/A'}</p>
            </>
          )}
        </div>

        <p className="text-center text-[#9CB0C7] text-sm mt-6">
          Need help? <a href="mailto:support@commerceindex.com" className="text-[#3EF1C7] hover:underline">Contact support</a>
        </p>
      </div>
    </div>
  );
}
